/**
 * Care Package room: the gift's own chip bar and first message. The chips are
 * lines the recipient would say, naming the people, never the product.
 *
 * Never more than two at once (spec principle 4). Stage 1 (round 2,
 * DESIGN-2 §34): "Draw our card" / "Draw my card" and "Tell your son what it
 * meant" (§39: it opens the reply card in place, in the chat). Stage 2
 * (R5.1): once their card is drawn, "Ask my own question" (§38) takes the
 * card's place; the second chip is the write-back, or once written the two of
 * them ("What do Chris's stars say about us?"). Once You & Chris has been made
 * (the server's `room.stars`, or shown in this visit), that is not offered
 * again: the chip opens it on her Mirror instead (2026-10-19). The server says
 * the same in `gift_room.offer`. No pronoun for the sender or the recipient,
 * ever (Chris, 2026-10-22): the name, or the relationship as signed.
 */

import { letterVersions } from '@/utils/gift-letter'

/**
 * The sender as the recipient would say it (2026-10-22, gift redesign §6):
 * a signature that names a relationship ("Your Son") becomes "your son"; a
 * name stays a name. Never a pronoun.
 */
export function senderAsSaid (gift = {}, text = {}) {
  const served = String((text && text.sender_ref) || '').trim()
  if (served) return served
  const from = String((gift && gift.from_name) || '').trim()
  if (!from) return ''
  if (/^your\s/i.test(from)) return from.toLowerCase()
  return from
}

/** Two or more people ("Mom & Dad", "Sam and Jo"): the pull says "us". */
// The server's room_words.is_plural, word for word: "The kids" and "My
// parents" are two or more people too (the pull line said "between you"
// while the chip said "Draw my card").
const PLURAL_WORDS = /\b(parents|family|kids|children|everyone|team|folks|guys|girls|boys|crew|gang|grandparents|sisters|brothers|friends|cousins|twins|y'all|all of you)\b/i
export function isPlural (forName = '') {
  const name = String(forName || '')
  return /\s(&|and|\+)\s|,/i.test(` ${name} `) || PLURAL_WORDS.test(name)
}

/**
 * "Draw our card" / "Draw my card" (DESIGN-2 §34). Fixed in code from the
 * names, never the model's words, so it cannot drift (the server's
 * `pull_label` follows the same rule; a stale one is ignored).
 */
// eslint-disable-next-line no-unused-vars
export function pullLabel (gift = {}, text = {}) {
  return isPlural(gift && gift.for_name) ? 'Draw our card' : 'Draw my card'
}

/** "Tell your son what it meant" (DESIGN-2 §38): the write-back chip. */
export function writeBackLabel (gift = {}, text = {}) {
  const said = senderAsSaid(gift, text)
  return said ? `Tell ${said} what it meant` : 'Write back'
}

/** Signed out, on a package already hers: the one way on (bug walk #18). */
export const SIGN_IN_LABEL = 'Sign in to pick up where you left off'

/** The funnel chip, once their card is drawn (DESIGN-2 §38). */
export const OWN_LABEL = 'Ask my own question'

/** The picker pill's eyebrow (§35) and the card panel's, which closes the loop (§37). */
export const heldQuestionEyebrow = 'Hold this question'
export const HELD_QUESTION_LABEL = 'The question you held'

const capital = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t)

/**
 * The Mystic's bridge into the two end chips (§38): a statement, never a
 * question. Once they have written back, only the offer of their own.
 */
export function bridgeLine (senderRef = '', { thanked = false } = {}) {
  const said = String(senderRef || '').trim()
  const offer = "if something of your own is on your mind, I'm here for that too."
  if (!said || thanked) return capital(offer)
  return `${capital(said)} will want to know how this landed. And ${offer}`
}

/** The Mystic's answer once the reply is sent (§39), a statement. */
export function repliedLine (senderRef = '') {
  const said = String(senderRef || '').trim()
  return said ? `That will mean a lot to ${said}. It's on its way.` : "That will mean a lot. It's on its way."
}

/**
 * Their slot in the picker's row and its tile on the table (§37): the name as
 * signed ("Mom & Dad"); Ours / Mine when it is longer than 12 characters.
 */
export function slotLabel (forName = '') {
  const name = String(forName || '').trim()
  if (name && name.length <= 12) return name
  return isPlural(name) ? 'Ours' : 'Mine'
}

/**
 * Inline text is for replies, chips are tools (Chris, 2026-10-27). Only the
 * room's reply, "What do Sam's stars say about us?" (a link in the Mystic's
 * last line), is said as their words and shows as their bubble. The chips are
 * tools and never echo: "Draw my card" (the picker and the pull line answer
 * it), the write-back (opens in place), the Mirror (a sheet) and "Ask my own
 * question" (a fresh session).
 */
const ECHO_KEYS = new Set(['gift_stars'])
export function chipEcho (key, chips = []) {
  if (!ECHO_KEYS.has(key)) return ''
  const chip = (Array.isArray(chips) ? chips : []).find((c) => c && c.key === key)
  return (chip && chip.label) || ''
}

/**
 * The chips for this gift, from the server's gift_room state. `text` is the
 * server's words for the room (`room` of care-package/open: `pull_label`, …).
 */
export function giftChips (gift = {}, room = {}, text = {}) {
  // The server's word for the sender ("your son", or a name); the app's own
  // reading of the signature when the server has none (an old client cache).
  const sender = (text && text.sender_ref) ? senderAsSaid(gift, text) : (gift.from_name || '').trim()
  const chips = []
  // Signed out, and the package is already hers (claimed by her account): the
  // room offers her way back in, never "Draw my card" again (gift bug walk
  // 2026-10-25 #18). The server sends `claimed` to a signed-out viewer only.
  // A copied link (door 'guest') has no sign-in to offer a stranger: as
  // before, the first act says "This one was opened already."
  if (room.claimed && (gift || {}).door !== 'guest') {
    chips.push({ key: 'gift_signin', label: SIGN_IN_LABEL, icon: 'fa-key', primary: true })
    return chips
  }
  if (room.deeper === 'drawn' && !room.thanked) {
    // Gift room redo (2026-10-29): after their card the write-back leads,
    // gold; their own question is the quiet second.
    chips.push({ key: 'gift_thank', label: writeBackLabel(gift, text), icon: 'fa-heart', primary: true })
    chips.push({ key: 'gift_own', label: OWN_LABEL, icon: 'fa-feather' })
    return chips
  }
  if (room.deeper === 'drawn') {
    chips.push({ key: 'gift_own', label: OWN_LABEL, icon: 'fa-feather', primary: true })
  } else {
    // The room opens on one action: their card. The write-back comes after
    // it, when they have something to say (Chris, 2026-10-23).
    chips.push({ key: 'gift_deeper', label: pullLabel(gift, text), icon: 'fa-wand-magic-sparkles', primary: true })
    return chips
  }
  if (!room.thanked) {
    // The reply card opens in place, in the chat (DESIGN-2 §39).
    chips.push({ key: 'gift_thank', label: writeBackLabel(gift, text), icon: 'fa-heart' })
  } else if (room.stars) {
    // You & the sender is already made: open it where it is kept.
    chips.push({ key: 'gift_mirror', label: sender ? `You & ${sender} on my Mirror` : 'The two of us on my Mirror', icon: 'fa-star' })
  } else {
    // Thanked: the sender's place in the bar becomes the two of them.
    // A question they would ask: a reply (a link in the Mystic's last line,
    // never a chip; 2026-10-27). The chat reads `reply`.
    chips.push({ key: 'gift_stars', label: sender ? `What do ${sender}'s stars say about us?` : 'What do our stars say?', icon: 'fa-star', reply: true })
  }
  return chips
}

/**
 * The chips the room offers right now (gift bug walk 2026-10-25 #4, #6):
 * none while Stage 3 is still asking (her birthday, then You & the sender),
 * and never one she already tapped under its own answer (Chris: "a tapped
 * chip is never offered again under its own answer"). `tapped` is the keys
 * tapped in this visit; the write-back is left out of it (its card can be
 * put away with "Not now", and the chip brings it back).
 */
export function roomChipsNow (chips = [], { tapped = [], stageOpen = false } = {}) {
  if (stageOpen) return []
  const gone = new Set((Array.isArray(tapped) ? tapped : []).filter((k) => k !== 'gift_thank'))
  return (Array.isArray(chips) ? chips : []).filter((c) => c && !gone.has(c.key))
}

/**
 * What the room already holds, rebuilt from the server's state when it opens
 * again (a reload, sign-in, another device; gift bug walk 2026-10-25 #6): her
 * card's panel (`gift_room.card`, the talk-card shape) and the bridge after
 * it; her write-back (`room.replied`) with the Mystic's thanks; and You & the
 * sender once it was made (`gift_room.stars`), as Stage 3 already answered.
 * In the order she did them: the card first (the room offers nothing else
 * before it). The arrival line and the SMS line are the page's own.
 * Returns message objects; `sentReply` builds the write-back message.
 */
export function rebuiltRoom ({ gift = {}, room = {}, text = {}, sentReply = null, starsRoom = null } = {}) {
  const out = []
  const said = senderAsSaid(gift || {}, text || {})
  const card = room && room.deeper === 'drawn' && room.card
  if (card && card.card) {
    out.push({
      text: '', type: 'reader', completed: true, _context: 'talk-card', _rebuilt: true,
      talk_card: {
        id: String(card.id || 'gift-card'), kind: card.kind || 'gift', card: card.card || '', image: card.image || '',
        reversed: card.reversed === true || card.reversed === 'true', question: card.question || '',
        line: card.line || '', reading: card.reading || '', read: true,
      },
    })
    const bridge = String((text && text.bridge) || '').trim() || bridgeLine(said)
    out.push({ text: bridge, type: 'reader', _context: 'gift-bridge' })
  }
  const replied = text && text.replied
  if (replied) {
    if (typeof sentReply === 'function') out.push(sentReply({ rating: replied.rating || 0, note: replied.note || '' }))
    out.push({ text: repliedLine(said), type: 'reader', _context: 'gift-replied' })
  }
  if (room && room.stars && starsRoom) {
    out.push({ text: '', type: 'reader', _context: 'stage-three', stage_three: { ...starsRoom, replay: true } })
  }
  return out
}

/**
 * The room's first message: the server's (`room.first_message`, written once
 * at preview time, so the preview and the room say the same words), or the
 * fallback. Never the note again.
 */
export function roomFirstMessage (gift = {}, text = {}) {
  const served = String((text && text.first_message) || '').trim()
  if (served) return served
  const name = String((gift && gift.for_name) || '').trim()
  const said = senderAsSaid(gift, text) || 'someone'
  const lead = name ? `${name}, ${said}` : said.charAt(0).toUpperCase() + said.slice(1)
  const theirs = senderAsSaid(gift, text) ? `${said}'s` : 'theirs'
  return `${lead} came to me with you in mind. These cards are what came up. When you're ready, draw one card of your own, and I'll read it beside ${theirs}.`
}

/**
 * The reply card's words (§7): the server's `respond`, filled with the
 * fallbacks wherever a field is missing.
 */
export const REPLY_WORDS = ['Not really', 'A little', 'Partly', 'Mostly', 'Exactly right']
export function replyText (gift = {}, respond = {}, text = {}) {
  const r = respond || {}
  const said = senderAsSaid(gift, text)
  const str = (v) => String(v || '').trim()
  const words = Array.isArray(r.words) && r.words.length === 5 && r.words.every((w) => str(w))
    ? r.words.map(str) : REPLY_WORDS
  return {
    eyebrow: str(r.eyebrow) || (said ? `To ${said}` : 'Write back'),
    // The server's fallback (2026-10-23): DESIGN §7's own example when there is a sender to name.
    ask: str(r.ask) || (said ? `Did ${said} get it right?` : 'Did this ring true?'),
    words,
    placeholder: str(r.placeholder) || (said ? `Tell ${said} what it meant to you…` : 'A line back…'),
    button: str(r.button) || (said ? `Send to ${said}` : 'Send'),
    // The sent bubble (DESIGN-2 §39): "TO YOUR SON … ✓ Sent to your son's Messages".
    sealedEyebrow: str(r.sealed) || (said ? `To ${said}` : 'You wrote back'),
    // Their sent message in the chat (round 2, §39): "TO YOUR SON".
    sentEyebrow: said ? `To ${said}` : (str(r.eyebrow) || 'You wrote back'),
    sealedNote: str(r.seen) || (said ? `Sent to ${said}'s Messages` : 'It is on its way'),
  }
}

/**
 * The gift room's letter (§5): the same GiftLetter the send screen's preview
 * draws, from the same data. `letter` is the server's letter for the room
 * (care-package/open's `letter`, else the reading-preview the room fetched
 * first: her version's title, passages, phrases, picture, the reader);
 * `loaded` is what the room's panel loaded, for anything the letter lacks
 * (an old package: the sender's text in the same letter, R4.12).
 * Returns GiftLetter's props.
 */
export function roomLetter ({ letter = null, gift = {}, loaded = {} } = {}) {
  const l = letter || {}
  const hers = letterVersions(l).hers
  const g = gift || {}
  const fromLoaded = (loaded.cards || []).map((c) => ({
    name: c.name || '', reversed: !!c.reversed, image_url: c.image_url || c.image || '',
    position: c.position || '', phrase: c.phrase || '', text: c.text || c.meaning || '',
  }))
  // The letter's cards, each filled from the loaded panel where it is empty.
  const cards = hers.cards.length
    ? hers.cards.map((c, i) => {
      const f = fromLoaded[i] || {}
      return { ...c, image_url: c.image_url || f.image_url || '', phrase: c.phrase || f.phrase || '',
        text: c.text || f.text || '', position: c.position || f.position || '' }
    })
    : fromLoaded
  return {
    image: l.image_url || loaded.image || '',
    note: String(g.note || g.gift_note || '').trim(),
    fromName: String(g.from_name || '').trim(),
    title: hers.title || loaded.title || '',
    // Hers only (the letter's image_caption): the loaded panel's caption is
    // the sender's, so it is never the fallback.
    caption: hers.caption || '',
    readerName: l.reader_name || loaded.readerName || '',
    readerAvatar: l.reader_avatar || loaded.readerAvatar || '',
    cards,
    summary: hers.summary || loaded.summary || '',
  }
}

/**
 * While her picker is up the chat holds the pull line alone (DESIGN 2026-10-22
 * §8, "Heard"): the Mystic's `pull_line` (reference `deeper-carry`) and what
 * follows it. The room's first message comes back above it once her card lands.
 * No pull line yet: everything, as it is.
 */
export function pickLineAlone (messages = []) {
  const ms = Array.isArray(messages) ? messages : []
  let at = -1
  for (let i = ms.length - 1; i >= 0; i--) {
    if (ms[i] && ms[i]._ref === 'deeper-carry') { at = i; break }
  }
  if (at <= 0) return ms
  // Their own tap, echoed just before it ("Draw our card"), stays with it.
  const prev = ms[at - 1]
  if (prev && prev.type === 'user' && prev._context === 'gift-echo') at -= 1
  return ms.slice(at)
}
